import React, { useEffect, useState } from "react";
import Stack from "@mui/material/Stack";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Autocomplete,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useDispatch } from "react-redux";

import { getSizeList } from "app/redux/slice/sizeSlice";
import { getAllSizeCharts } from "app/redux/slice/sizeChartSlice";

const AddSizeChart = ({ value, onClose, handleSubmit }) => {
  const dispatch = useDispatch();
  const mobileView = useMediaQuery("(max-width:575px)");

  const [sizes, setSizes] = useState([]);
  const [loadingSizes, setLoadingSizes] = useState(true);
  // sizeId -> name of the chart already holding it (this chart's own excluded).
  const [takenBy, setTakenBy] = useState({});

  /**
   * The dropdown needs the whole size master, and `size/list` only paginates
   * when a page is passed - so a large perPage is sent deliberately rather than
   * relying on its default, which would return a partial list.
   *
   * The charts are loaded too: a size belongs to one chart only, so the ones
   * already taken are shown but not selectable.
   */
  useEffect(() => {
    Promise.all([
      dispatch(
        getSizeList({ page: 1, perPage: 1000, field: "name", sort: 1 })
      ).unwrap(),
      dispatch(getAllSizeCharts()).unwrap(),
    ])
      .then(([sizeRes, chartRes]) => {
        setSizes(sizeRes?.data?.data?.sizeList ?? []);

        const taken = {};
        (chartRes?.data?.data?.sizeChartList ?? []).forEach((chart) => {
          if (chart._id === value?._id) return;
          (chart.sizeIds ?? []).forEach((size) => {
            const id = typeof size === "string" ? size : size?._id;
            if (id) taken[id] = chart.name;
          });
        });
        setTakenBy(taken);
      })
      .finally(() => setLoadingSizes(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter size chart name"),
    sizeIds: Yup.array().min(1, "Please select at least one size"),
    order: Yup.number()
      .transform((v, original) => (original === "" ? null : v))
      .nullable()
      .integer("Order must be a whole number")
      .min(0, "Order cannot be negative"),
  });

  // On edit the API sends sizeIds populated, so ids are pulled back out of it.
  const selectedIds = (value?.sizeIds ?? []).map((size) =>
    typeof size === "string" ? size : size?._id
  );

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: value?.name || "",
        sizeIds: selectedIds,
        // Blank rather than 0 - an empty box means "no position set".
        order: value?.order ?? "",
      }}
      validationSchema={validationSchema}
      onSubmit={(data, { setSubmitting }) => {
        setSubmitting(true);
        handleSubmit(data).finally(() => setSubmitting(false));
      }}
    >
      {({ isSubmitting, values, setFieldValue, touched, errors }) => (
        <Form>
          <JumboAppTextField
            sx={{ mb: 2, mt: 1 }}
            fullWidth
            name="name"
            label="Size chart name*"
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={sizes}
            loading={loadingSizes}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, selected) =>
              option?._id === selected?._id
            }
            value={sizes.filter((size) => values.sizeIds.includes(size._id))}
            onChange={(_, selected) =>
              setFieldValue(
                "sizeIds",
                selected.map((size) => size._id)
              )
            }
            // A size lives in one chart only, so the ones another chart holds
            // stay visible but cannot be picked - and say who has them.
            getOptionDisabled={(option) => Boolean(takenBy[option._id])}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option._id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  disabled={Boolean(takenBy[option._id])}
                  sx={{ mr: 1 }}
                />
                {option.name}
                {takenBy[option._id] && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    - already in "{takenBy[option._id]}"
                  </Typography>
                )}
              </li>
            )}
            renderTags={(selected, getTagProps) =>
              selected.map((option, index) => (
                <Chip
                  size="small"
                  label={option.name}
                  {...getTagProps({ index })}
                  key={option._id}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sizes*"
                placeholder={values.sizeIds.length ? "" : "Select sizes"}
                error={Boolean(touched.sizeIds && errors.sizeIds)}
                helperText={touched.sizeIds && errors.sizeIds}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSizes ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            sx={{ mb: 2 }}
          />

          <JumboAppTextField
            sx={{ mb: 3 }}
            fullWidth
            name="order"
            type="number"
            label="Order"
            inputProps={{ min: 0 }}
            helperText="Optional - lower numbers show first. Leave blank to place it at the end."
          />

          <Stack
            spacing={{ xs: 1, sm: 2 }}
            direction="row"
            justifyContent="flex-end"
          >
            <LoadingButton
              variant="outlined"
              color="secondary"
              onClick={onClose}
              size="large"
              fullWidth={mobileView}
            >
              Cancel
            </LoadingButton>
            <LoadingButton
              type="submit"
              variant="contained"
              size="large"
              sx={{ mb: 3 }}
              loading={isSubmitting}
              loadingIndicator={<CircularProgress size={20} />}
              fullWidth={mobileView}
            >
              Save
            </LoadingButton>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default AddSizeChart;
